import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Handles pre-order variant button text updates.
 * When a pre-order variant is selected, updates the "Add to Cart" button
 * to display the pre-order message (e.g., "PRE-ORDER EARLY MAR 2026").
 *
 * @extends Component
 */
class PreorderButtonHandler extends Component {
  /** @type {string} */
  #defaultButtonText = 'Add to Cart';

  /** @type {HTMLElement | null} */
  #buttonTextElement = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Find the button text element
    this.#buttonTextElement = this.querySelector('.add-to-cart-text__content span span');
    
    if (this.#buttonTextElement) {
      this.#defaultButtonText = this.#buttonTextElement.textContent?.trim() || 'Add to Cart';
    }

    const { signal } = this.#abortController;
    
    // Listen for variant changes
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantChange, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    
    // Initialize with current variant
    this.#updateButtonText();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles variant selection events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantChange = (event) => {
    // Small delay to ensure DOM has updated
    requestAnimationFrame(() => {
      this.#updateButtonText();
    });
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    requestAnimationFrame(() => {
      this.#updateButtonText();
    });
  };

  /**
   * Updates the button text based on selected variant
   */
  #updateButtonText() {
    if (!this.#buttonTextElement) return;

    const preorderMessage = this.#getSelectedPreorderMessage();
    
    if (preorderMessage) {
      this.#buttonTextElement.textContent = preorderMessage;
    } else {
      this.#buttonTextElement.textContent = this.#defaultButtonText;
    }
  }

  /**
   * Gets the pre-order message from the currently selected variant
   * @returns {string | null} The pre-order message or null
   */
  #getSelectedPreorderMessage() {
    // Find the closest variant picker (works for PDP, quick add modal, etc.)
    let variantPicker = this.closest('product-form-component, quick-add-with-variant-picker')?.querySelector('variant-picker');
    
    // Fallback to any variant picker on the page
    if (!variantPicker) {
      variantPicker = document.querySelector('variant-picker');
    }
    
    if (!variantPicker) return null;

    // Check all checked radio inputs or selected options
    const checkedInputs = variantPicker.querySelectorAll('input[type="radio"]:checked, option:checked');
    
    for (const input of checkedInputs) {
      const preorderMessage = input.dataset.preorderMessage;
      if (preorderMessage) {
        return preorderMessage;
      }
    }

    return null;
  }
}

if (!customElements.get('preorder-button-handler')) {
  customElements.define('preorder-button-handler', PreorderButtonHandler);
}

export default PreorderButtonHandler;
